import React, { useEffect, useMemo, useState } from 'react';
import TopNav from '../components/TopNav';
import SideIcons from '../components/SideIcons';
import ComposeModal from '../components/ComposeModal';
import Footer from '../components/Footer';
import { postsAPI, analyticsAPI } from '../api';
import './Analytics.css';

const Analytics = () => {
  const [dateRange, setDateRange] = useState('Last 7 Days');
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [activePlatforms, setActivePlatforms] = useState(null); // null = default/allowed, [] = none supported/selected
  const [kpis, setKpis] = useState({
    followers: 0,
    engagement_rate: null,
    reach: 0,
    profile_views: 0
  });
  const [topPosts, setTopPosts] = useState([]);

  const handleComposeClick = () => {
    setIsComposeModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsComposeModalOpen(false);
  };

  const handlePlatformToggle = (platforms) => {
    setActivePlatforms(platforms);
  };

  const range = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    let start = new Date(now);
    if (dateRange === 'Last 7 Days') {
      start.setDate(start.getDate() - 7);
    } else if (dateRange === 'Last 30 Days') {
      start.setDate(start.getDate() - 30);
    } else if (dateRange === 'Last 90 Days') {
      start.setDate(start.getDate() - 90);
    } else if (dateRange === 'Year to Date') {
      start = new Date(now.getFullYear(), 0, 1);
    } else {
      start.setDate(start.getDate() - 7);
    }

    const toDateOnly = (d) => d.toISOString().split('T')[0];
    return {
      start: toDateOnly(start),
      end: toDateOnly(end)
    };
  }, [dateRange]);

  useEffect(() => {
    const load = async () => {
      // Fetch ALL platforms - activePlatforms is UI filter only, applied client-side after fetch
      try {
        const [overview, top] = await Promise.all([
          analyticsAPI.getOverview({ start: range.start, end: range.end }),
          analyticsAPI.getTopPosts({ start: range.start, end: range.end, limit: 25 })
        ]);
        setKpis(overview.kpis || { followers: 0, engagement_rate: null, reach: 0, profile_views: 0 });
        // Filter top posts client-side by activePlatforms for display
        const allTopPosts = top.items || [];
        const filteredTopPosts = Array.isArray(activePlatforms) && activePlatforms.length > 0
          ? allTopPosts.filter(post => activePlatforms.includes(post.platform))
          : allTopPosts;
        setTopPosts(filteredTopPosts);
      } catch (error) {
        console.error('Error loading analytics:', error);
        setKpis({ followers: 0, engagement_rate: null, reach: 0, profile_views: 0 });
        setTopPosts([]);
      }
    };

    load();
  }, [activePlatforms, range.end, range.start]);

  const handleSubmitPost = async (postData, postId = null) => {
    try {
      if (postId) {
        await postsAPI.update(postId, postData);
      } else {
        await postsAPI.create(postData);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      throw error;
    }
  };

  const fmtInt = (n) => {
    try {
      return (n || 0).toLocaleString();
    } catch {
      return String(n || 0);
    }
  };

  const fmtPct = (v) => {
    if (v === null || v === undefined) return '—';
    const num = Number(v);
    if (Number.isNaN(num)) return '—';
    return `${(num * 100).toFixed(1)}%`;
  };

  const kpiData = [
    { label: 'Followers', value: fmtInt(kpis.followers), growth: '', positive: true },
    { label: 'Engagement Rate', value: fmtPct(kpis.engagement_rate), growth: '', positive: true },
    { label: 'Total Reach', value: fmtInt(kpis.reach), growth: '', positive: true },
    { label: 'Profile Views', value: fmtInt(kpis.profile_views), growth: '', positive: true }
  ];

  const downloadKPI = (label, value, growth) => {
    const data = `KPI: ${label}\nValue: ${value}\nGrowth: ${growth}\nDate Range: ${dateRange}`;
    const blob = new Blob([data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${label.replace(/\s+/g, '_')}_Report.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="analytics-page">
      <TopNav activePage="analytics" onComposeClick={handleComposeClick} />
      <SideIcons
        requireCapability="can_insights"
        onPlatformToggle={handlePlatformToggle}
        activePlatforms={activePlatforms}
      />

      <main className="analytics-container">
        {Array.isArray(activePlatforms) && activePlatforms.length === 0 ? (
          <div className="feature-unavailable" role="status" aria-live="polite">
            <div className="feature-unavailable-card">
              <h2>Analytics unavailable</h2>
              <p>
                None of your connected account types support analytics/insights for the selected platforms.
              </p>
              <p>Connect a supported account in Settings.</p>
            </div>
          </div>
        ) : null}

        {/* HEADER */}
        <div className="analytics-header">
          <h1>Analytics Overview</h1>
          <select 
            className="date-range" 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Last 90 Days</option>
            <option>Year to Date</option>
          </select>
        </div>

        {/* KPI CARDS */}
        <section className="kpi-grid">
          {kpiData.map((kpi, index) => (
            <div key={index} className="kpi-card">
              <p className="label">{kpi.label}</p>
              <h2>{kpi.value}</h2>
              {kpi.growth ? (
                <span className={`growth ${kpi.positive ? 'positive' : 'negative'}`}>
                  {kpi.growth}
                </span>
              ) : null}
              <button 
                className="download-btn"
                onClick={() => downloadKPI(kpi.label, kpi.value, kpi.growth)}
              >
                Download
              </button>
            </div>
          ))}
        </section>

        {/* CHARTS PLACEHOLDER */}
        <section className="charts-grid">
          <div className="chart-card">
            <h3>Follower Growth</h3>
            <div className="chart-placeholder">
              <p>Chart visualization would go here</p>
            </div>
          </div>

          <div className="chart-card">
            <h3>Engagement Rate Over Time</h3>
            <div className="chart-placeholder">
              <p>Chart visualization would go here</p>
            </div>
          </div>
        </section>

        {/* TOP POSTS TABLE */}
        <section className="top-posts">
          <h2>Top Performing Posts</h2>
          <table className="posts-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Date</th>
                <th>Engagement</th>
                <th>Reach</th>
              </tr>
            </thead>
            <tbody>
              {topPosts.map((post, index) => {
                const platformLabel = (post.platform || '').toString();
                const nicePlatform = platformLabel ? platformLabel[0].toUpperCase() + platformLabel.slice(1) : '—';
                return (
                  <tr key={index}>
                    <td>{nicePlatform}</td>
                    <td>{post.date || '—'}</td>
                    <td>{post.engagement_rate !== undefined ? fmtPct(post.engagement_rate) : '—'}</td>
                    <td>{post.reach !== undefined ? fmtInt(post.reach) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </main>

      <ComposeModal 
        isOpen={isComposeModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPost}
      />
      <Footer />
    </div>
  );
};

export default Analytics;
